import { useCallback, useMemo, useRef, useState } from 'react';
import { BULK_SELECTION_MODE } from 'framework-listing-lib/constants/table';
import { useDraggedObject } from 'framework-listing-lib/internal/Table/providers/DragProvider';
import FolderDropZone from './FolderDropZone';
import { DRAG_EVENT_KEY } from '../constants/folders';
import { isFoldersDragItemEvent } from '../utils/dragAndDrop';
import { jsx as _jsx } from "react/jsx-runtime";
const convertToInt = input => {
  return typeof input === 'string' ? parseInt(input, 10) : input;
};
export default function FolderNavItemDropTargetV2({
  folder,
  children,
  folderItemWidth,
  onDrop
}) {
  /**
   * onDragLeave is triggered when hovering over a child element, making it so the
   * hover styles would only be visible for a split second. In order to solve this
   * we can take advantage of the fact onDragEnter fires for the children as well.
   * the amount of enter events are 0 then there should be no hover styles, otherwise there
   * should be.
   */
  const enterEvents = useRef(0);
  const [hovering, setHovering] = useState(false);
  const {
    currentDragData
  } = useDraggedObject();
  const canDropHere = useMemo(() => {
    if (!currentDragData) {
      return false;
    }
    return currentDragData.previousFolderId !== folder.objectId && currentDragData.selectionMode !== 'all';
  }, [currentDragData, folder.objectId]);
  const handleDragOver = useCallback(e => {
    e.preventDefault();
  }, []);
  const handleOnDrop = useCallback(e => {
    if (!isFoldersDragItemEvent(e)) {
      setHovering(false);
      return;
    }
    const item = JSON.parse(e.dataTransfer.getData(DRAG_EVENT_KEY));
    const canDrop = item.previousFolderId !== folder.objectId && item.selectionMode !== 'all';
    if (!canDrop) {
      return;
    }
    const selectionMode = item.selectionMode;
    if (selectionMode === BULK_SELECTION_MODE.ALL) {
      return;
    }

    /**
     * True when:
     *  - selecting a single row (no bulk actions)
     *  - selecting a partial amount of rows but dragged row is not selected
     */
    const useObjectId = selectionMode === BULK_SELECTION_MODE.NONE || selectionMode === BULK_SELECTION_MODE.PARTIAL && !item.isSelected;

    /**
     * True when
     *   - selecting all rows on page
     *   - selecing a partial amount of rows and dragged row is selected
     */
    const useSelectedIds = selectionMode === BULK_SELECTION_MODE.ALL_ON_PAGE || selectionMode === BULK_SELECTION_MODE.PARTIAL && item.isSelected;
    let assetIds = [];
    if (useObjectId) {
      assetIds = [convertToInt(item.objectId)];
    } else if (useSelectedIds) {
      assetIds = item.selectedIds;
    }
    if (assetIds.length === 0) {
      return;
    }
    onDrop(assetIds, item.previousFolderId, convertToInt(folder.objectId)).then(() => {}).catch(() => {});
    enterEvents.current = 0;
    setHovering(false);
  }, [folder.objectId, onDrop]);
  const handleOnDragEnter = useCallback(e => {
    if (!isFoldersDragItemEvent(e)) {
      setHovering(false);
      return;
    }
    enterEvents.current += 1;
    setHovering(true);
  }, []);
  const handleOnDragLeave = useCallback(e => {
    if (!isFoldersDragItemEvent(e)) {
      setHovering(false);
      return;
    }
    enterEvents.current -= 1;
    if (enterEvents.current === 0) {
      setHovering(false);
    }
  }, []);
  /**
   * All drag and drop methods are conditional since the browser
   * automatically styles the cursor and item being dragged, assuming
   * it can be always be dropped when it can't if it is already in that folder
   */
  return /*#__PURE__*/_jsx("div", {
    style: {
      overflow: 'hidden',
      width: folderItemWidth,
      height: '100%'
    },
    onDragOver: canDropHere ? handleDragOver : undefined,
    onDrop: canDropHere ? handleOnDrop : undefined,
    onDragEnter: canDropHere ? handleOnDragEnter : undefined,
    onDragLeave: canDropHere ? handleOnDragLeave : undefined,
    children: /*#__PURE__*/_jsx(FolderDropZone, {
      use: hovering ? 'hover' : 'default',
      children: children
    })
  });
}